import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Fuel, CreditCard, Loader2, CheckCircle, ExternalLink, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pas-eth`;

/**
 * BuyGasCard - Client pays via Stripe for gas (ETH) from platform's funding wallet
 * Shows clear breakdown: USD amount, platform fee, ETH received
 */
export const BuyGasCard = ({ token, walletAddress }) => {
  const [usdAmount, setUsdAmount] = useState(10);
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [purchases, setPurchases] = useState([]);

  // Load previous purchases
  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/gas/purchases`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setPurchases(r.data.purchases || []))
      .catch(() => {});
  }, [token]);

  // Get quote when amount changes
  useEffect(() => {
    if (!token || usdAmount < 5) {
      setQuote(null);
      return;
    }
    
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/gas/quote?usd_amount=${usdAmount}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setQuote(r.data);
      } catch (e) {
        setQuote(null);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [token, usdAmount]);

  const handleCheckout = async () => {
    if (!quote || !quote.can_fulfill) return;
    
    setCheckingOut(true);
    try {
      const r = await axios.post(`${API}/gas/checkout`, 
        { usd_amount: usdAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.location.href = r.data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Checkout failed');
      setCheckingOut(false);
    }
  };

  const presetAmounts = [5, 10, 25, 50];

  if (!walletAddress) {
    return (
      <div className="rounded-lg border border-slate-600 bg-slate-800/50 p-4" data-testid="buy-gas-no-wallet">
        <p className="text-sm text-slate-400">You need a wallet to buy gas.</p>
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-orange-500/50 bg-orange-500/5 p-5" data-testid="buy-gas-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-orange-500/20 rounded-lg">
          <Fuel className="w-5 h-5 text-orange-400" />
        </div>
        <div>
          <h3 className="text-white font-semibold">Buy Gas (ETH)</h3>
          <p className="text-xs text-slate-400">Pay with card, receive ETH for transactions</p>
        </div>
      </div>

      {/* Preset amounts */}
      <div className="flex gap-2 mb-4">
        {presetAmounts.map(amt => (
          <button
            key={amt}
            onClick={() => setUsdAmount(amt)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              usdAmount === amt 
                ? 'bg-orange-500 text-white' 
                : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
            }`}
          >
            ${amt}
          </button>
        ))}
        <input
          type="number"
          value={usdAmount}
          onChange={(e) => setUsdAmount(parseInt(e.target.value) || '')}
          onBlur={(e) => {
            const val = parseInt(e.target.value) || 10;
            setUsdAmount(Math.max(5, Math.min(100, val)));
          }}
          min={5}
          max={100}
          className="w-20 px-2 py-1.5 bg-slate-700 border border-slate-600 rounded-lg text-white text-sm text-center"
          data-testid="buy-gas-amount-input"
        />
      </div>

      {/* Quote display */}
      {loading ? (
        <div className="py-4 flex justify-center">
          <Loader2 className="w-5 h-5 text-orange-400 animate-spin" />
        </div>
      ) : quote ? (
        <div className="bg-slate-900/60 rounded-lg p-4 space-y-2 mb-4" data-testid="buy-gas-quote">
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">You pay</span>
            <span className="text-white font-medium">${quote.usd_amount.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-slate-400">Platform fee ({quote.platform_fee_pct}%)</span>
            <span className="text-amber-300">-${quote.platform_fee_usd.toFixed(2)}</span>
          </div>
          <div className="border-t border-slate-700 pt-2 flex justify-between text-sm">
            <span className="text-slate-400">ETH value</span>
            <span className="text-slate-300">${quote.net_usd.toFixed(2)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-400">You receive</span>
            <span className="text-emerald-400 font-bold text-lg">{quote.eth_amount.toFixed(6)} ETH</span>
          </div>
          <p className="text-xs text-slate-500 text-right">@ ${quote.eth_price_usd.toLocaleString()}/ETH</p>
          
          {!quote.can_fulfill && (
            <div className="mt-2 flex items-center gap-2 text-amber-400 text-xs">
              <AlertCircle className="w-4 h-4" />
              <span>Gas supply low. Try a smaller amount.</span>
            </div>
          )}
        </div>
      ) : usdAmount >= 5 ? (
        <div className="py-4 text-center text-slate-500 text-sm">
          Unable to get quote
        </div>
      ) : null}

      {/* Checkout button */}
      <button
        onClick={handleCheckout}
        disabled={!quote || !quote.can_fulfill || checkingOut}
        className="w-full py-3 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
        data-testid="buy-gas-checkout-btn"
      >
        {checkingOut ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <CreditCard className="w-4 h-4" />
        )}
        Pay ${usdAmount} with Card
      </button>

      {/* Recent purchases */}
      {purchases.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="buy-gas-purchases">
          <p className="text-xs text-slate-500 font-medium">Recent Gas Purchases</p>
          {purchases.slice(0, 3).map(p => (
            <div 
              key={p.purchase_id}
              className="flex items-center justify-between text-xs bg-slate-900/40 rounded-lg px-3 py-2"
            >
              <div>
                <span className="text-slate-300">${p.usd_amount}</span>
                <span className="text-slate-500 mx-1">→</span>
                <span className="text-emerald-400">{p.eth_amount?.toFixed(6)} ETH</span>
              </div>
              <div className="flex items-center gap-2">
                {p.status === 'completed' ? (
                  <>
                    <CheckCircle className="w-3.5 h-3.5 text-emerald-400" />
                    {p.explorer_url && (
                      <a href={p.explorer_url} target="_blank" rel="noreferrer" className="text-blue-400">
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </>
                ) : p.status === 'failed' ? (
                  <span className="text-red-400">Failed</span>
                ) : (
                  <Loader2 className="w-3.5 h-3.5 text-amber-400 animate-spin" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
